import { useCallback } from 'react';

export function FormattingToolbar({
  textareaRef,
  content,
  setContent,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  content: string;
  setContent: (v: string) => void;
}) {
  const wrapSelection = useCallback(
    (before: string, after: string) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const selected = content.slice(start, end);
      const replacement = selected ? `${before}${selected}${after}` : `${before}text${after}`;
      const newContent = content.slice(0, start) + replacement + content.slice(end);
      setContent(newContent);
      requestAnimationFrame(() => {
        ta.focus();
        if (selected) {
          ta.setSelectionRange(start + before.length, start + before.length + selected.length);
        } else {
          ta.setSelectionRange(start + before.length, start + before.length + 4);
        }
      });
    },
    [textareaRef, content, setContent],
  );

  const prefixLine = useCallback(
    (prefix: string) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const pos = ta.selectionStart;
      // Find start of current line
      const lineStart = content.lastIndexOf('\n', pos - 1) + 1;
      const newContent = content.slice(0, lineStart) + prefix + content.slice(lineStart);
      setContent(newContent);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(pos + prefix.length, pos + prefix.length);
      });
    },
    [textareaRef, content, setContent],
  );

  return (
    <div role="toolbar" aria-label="Text formatting" className="flex items-center gap-0.5 border-b border-gray-100 px-4 py-1 dark:border-gray-800">
      <button
        type="button"
        onClick={() => wrapSelection('**', '**')}
        className="rounded px-1.5 py-0.5 text-xs font-bold text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Bold"
        aria-label="Bold"
      >
        B
      </button>
      <button
        type="button"
        onClick={() => wrapSelection('_', '_')}
        className="rounded px-1.5 py-0.5 text-xs italic text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Italic"
        aria-label="Italic"
      >
        I
      </button>
      <button
        type="button"
        onClick={() => prefixLine('### ')}
        className="rounded px-1.5 py-0.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Heading"
        aria-label="Heading"
      >
        H
      </button>
      <div className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" />
      <button
        type="button"
        onClick={() => prefixLine('- ')}
        className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Bullet list"
        aria-label="Bullet list"
      >
        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => prefixLine('1. ')}
        className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Numbered list"
        aria-label="Numbered list"
      >
        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => wrapSelection('`', '`')}
        className="rounded px-1.5 py-0.5 font-mono text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        title="Inline code"
        aria-label="Inline code"
      >
        {'</>'}
      </button>
    </div>
  );
}
